import { createContext, FC, useContext, useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import _ from 'lodash';
import { useGetHandlingRequestCountsQuery } from 'store/slices/apiV1/inventory';
import { useGetConfirmedPurchaseLinesQuery } from 'store/slices/apiV1/purchasing';
import { useGetUserQuery } from 'store/slices/apiV1/user';
import { pickTypeMap } from 'types/inventory';
import useLocalStorage from 'hooks/useLocalStorage';
import {
  warehouseDefaults,
  warehouseStorageName,
} from 'types/warehouseSettings';
import { convertDate } from 'utils/dates';
import { Org } from 'types/org';
import { OrgSupplier } from 'types/suppliers';
import {
  useGetShipmentsToReceive,
  ShipmentToReceive,
} from 'views/warehouse/hooks/useGetShipmentsToReceive';

type HandlingRequestStats = {
  type: string;
  count: number;
};

export type GroupedPurchaseLines = {
  id: string;
  supplier: OrgSupplier;
  customer: Org | undefined;
  trackingNumber: string;
  estimatedDelivery: string | Date;
  purchaseNumber: string | null | undefined;
}[];

interface DashboardContextType {
  handlingRequestStats: HandlingRequestStats[];
  shipmentsToReceive: ShipmentToReceive[];
  groupedPurchaseLines: GroupedPurchaseLines[];
  isLoadingPurchaseLines: boolean;
  isFetchingPurchaseLines: boolean;
  isLoadingPurchaseLinesSuccess: boolean;
  isLoadingPurchaseLinesError: boolean;
  isFetchingHandlingRequestsCounts: boolean;
  isGetShipmentsToReceiveSuccess: boolean;
  isFetchingShipmentsToReceive: boolean;
  isFetchingShipmentsToReceiveError: boolean;
  refetchHandlingRequestsCounts: () => void;
  refetchShipmentsToReceive: () => void;
  refetchPurchaseLines: () => void;
}

const DashboardContext = createContext<DashboardContextType | undefined>(
  undefined
);

export const DashboardProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [settings] = useLocalStorage(warehouseStorageName, warehouseDefaults);

  const { data: user } = useGetUserQuery(undefined);

  const {
    data: handlingRequestsCounts = {},
    isFetching: isFetchingHandlingRequestsCounts,
    refetch: refetchHandlingRequestsCounts,
  } = useGetHandlingRequestCountsQuery(
    settings.facility && settings.showHandlingDashboard
      ? {
          facilityId: settings.facility,
          sandbox: settings.sandboxMode,
          includeDryingMslResets: true,
        }
      : skipToken
  );

  const handlingRequestStats = useMemo(() => {
    const updatedHandlingRequestsCounts = _.mapKeys(
      handlingRequestsCounts,
      (value, key) => _.snakeCase(key)
    );
    return _.chain(updatedHandlingRequestsCounts)
      .map((count, type) => ({
        type,
        count:
          type === 'msl_reset'
            ? count - updatedHandlingRequestsCounts.msl_reset_drying
            : count,
      }))
      .filter(({ type }) => _.has(pickTypeMap, type))
      .filter(({ count }) => count > 0)
      .value();
  }, [handlingRequestsCounts]);

  const {
    shipmentsToReceive,
    isGetShipmentsToReceiveSuccess,
    isFetchingShipmentsToReceive,
    isFetchingShipmentsToReceiveError,
    refetchShipmentsToReceive,
  } = useGetShipmentsToReceive();

  const {
    data: purchaseLines,
    isLoading: isLoadingPurchaseLines,
    isFetching: isFetchingPurchaseLines,
    isSuccess: isLoadingPurchaseLinesSuccess,
    isError: isLoadingPurchaseLinesError,
    refetch: refetchPurchaseLines,
  } = useGetConfirmedPurchaseLinesQuery(
    settings.facility && settings.showReceiveDashboard
      ? {
          facilityId: settings.facility,
          sandbox: settings.sandboxMode,
        }
      : skipToken
  );

  const groupedPurchaseLines = useMemo(
    () =>
      _.chain(purchaseLines || [])
        .filter(
          (pl) =>
            _.find(user?.orgs, ['id', pl.stockLots[0]?.org])?.sandbox ===
            settings.sandboxMode
        )
        .filter((pl) => !!pl.shippedAt)
        .map((pl) => ({
          id: pl.id,
          supplier: pl.orgSupplier,
          customer: _.find(user?.orgs, ['id', pl.stockLots[0]?.org]),
          trackingNumber: _.chain(pl.events)
            .map('trackingNumber')
            .uniq()
            .compact()
            .join(', ')
            .value(),
          estimatedDelivery: pl.estimatedDelivery
            ? convertDate(pl.estimatedDelivery)
            : '',
          purchaseNumber: pl?.purchase?.poNumber,
        }))
        .groupBy('trackingNumber')
        .values()
        .sortBy((plg) => plg[0].estimatedDelivery)
        .value(),
    [purchaseLines, user, settings.sandboxMode]
  );

  return (
    <DashboardContext.Provider
      value={{
        handlingRequestStats,
        shipmentsToReceive,
        groupedPurchaseLines,
        isLoadingPurchaseLines,
        isFetchingPurchaseLines,
        isLoadingPurchaseLinesSuccess,
        isLoadingPurchaseLinesError,
        isGetShipmentsToReceiveSuccess,
        isFetchingShipmentsToReceive,
        isFetchingShipmentsToReceiveError,
        isFetchingHandlingRequestsCounts,
        refetchHandlingRequestsCounts,
        refetchShipmentsToReceive,
        refetchPurchaseLines,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboardContext = () => {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error(
      'useDashboardContext must be used within a DashboardProvider'
    );
  }
  return context;
};
